import { BellRingingIcon } from '@phosphor-icons/react';
import { createElement } from 'react';
/*
  Both deep imports rather than the feature/component barrels.

  RootStore wires this notifier at construction, which makes every module
  reachable from here part of the eager entry payload. `@/components/pos`
  re-exports the whole POS component set and `@/features/pos/device` re-exports
  DevicePage, PrinterSettings and the printer adapter — none of which a chime
  needs, and none of which belong on the cashier's first paint.
*/
import { showStatusToast } from '@/components/pos/status-toast';
import { formatRs } from '@/components/pos/format';
import { isSoundEnabled } from '@/features/pos/device/prefs';
import type { RealtimeNotifier } from '@/stores/RealtimeStore';
import type { NewOrderHint } from '@/realtime/events';
import type { Order } from '@/types/orders';
import chimeUrl from './assets/new-order-chime.wav';

async function showSystemOrderNotification(
  id: number,
  orderNumber: string | null,
  body: string,
): Promise<void> {
  if (
    typeof window === 'undefined' ||
    !('Notification' in window) ||
    Notification.permission !== 'granted' ||
    (document.visibilityState === 'visible' && document.hasFocus())
  ) {
    return;
  }

  const title = orderNumber ? `New order #${orderNumber}` : 'New website order';
  const options: NotificationOptions = {
    body,
    tag: `order-${id}`,
    icon: '/icons/pwa-192.png',
    badge: '/icons/pwa-192.png',
    data: { url: `/orders/${id}` },
  };

  try {
    const registration =
      'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
    if (registration) {
      await registration.showNotification(title, options);
      return;
    }

    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
      window.location.assign(`/orders/${id}`);
      notification.close();
    };
  } catch {
    // The in-app toast and bell still announce the order.
  }
}

/**
 * New-website-order notifier (V1-05). The sound is a SMALL BUNDLED LOCAL
 * asset (assets/new-order-chime.wav, ~17 KB, 0.55 s, generated — no external
 * fetch, works offline-cached like any static asset). Browser autoplay
 * denial (no user gesture yet) resolves `playChime()` to false and
 * RealtimeStore falls back to `onChimeBlocked` → StatusToast
 * (06-realtime.md §4).
 *
 * Lead wiring: `new RealtimeStore({ orders, notifier: createOrderNotifier() })`.
 */
export function createOrderNotifier(): RealtimeNotifier {
  let audio: HTMLAudioElement | null = null;
  let audioContext: AudioContext | null = null;

  const playBellTone = async (): Promise<boolean> => {
    if (typeof window === 'undefined') return false;
    const AudioContextConstructor =
      window.AudioContext ??
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextConstructor) return false;

    try {
      audioContext ??= new AudioContextConstructor();
      if (audioContext.state === 'suspended') await audioContext.resume();
      if (audioContext.state !== 'running') return false;

      const start = audioContext.currentTime + 0.02;
      const master = audioContext.createGain();
      master.gain.setValueAtTime(0.0001, start);
      master.gain.exponentialRampToValueAtTime(0.32, start + 0.012);
      master.gain.exponentialRampToValueAtTime(0.0001, start + 1.55);
      master.connect(audioContext.destination);

      const strike = (frequency: number, at: number, strength: number) => {
        const partials = [
          { ratio: 1, level: 1, type: 'sine' as OscillatorType },
          { ratio: 2.01, level: 0.28, type: 'sine' as OscillatorType },
          { ratio: 3.92, level: 0.09, type: 'triangle' as OscillatorType },
        ];

        for (const partial of partials) {
          const oscillator = audioContext!.createOscillator();
          const envelope = audioContext!.createGain();
          oscillator.type = partial.type;
          oscillator.frequency.setValueAtTime(frequency * partial.ratio, at);
          envelope.gain.setValueAtTime(0.0001, at);
          envelope.gain.exponentialRampToValueAtTime(
            Math.max(0.0001, strength * partial.level),
            at + 0.008,
          );
          envelope.gain.exponentialRampToValueAtTime(0.0001, at + 1.15);
          oscillator.connect(envelope);
          envelope.connect(master);
          oscillator.start(at);
          oscillator.stop(at + 1.2);
        }
      };

      // A clear descending shop-bell interval: bright enough to cut through a
      // counter, but with sine-heavy harmonics so it does not sound like an alarm.
      strike(1318.51, start, 0.55);
      strike(880, start + 0.24, 0.42);
      return true;
    } catch {
      return false;
    }
  };

  const showNewOrderNotification = (orders: readonly Order[]): void => {
    const first = orders[0];
    const customer = first?.customerName.trim();
    const detail = first
      ? [`#${first.orderNumber}`, customer || null, formatRs(first.totalAmount)]
          .filter(Boolean)
          .join(' · ')
      : undefined;

    showStatusToast({
      icon: createElement(BellRingingIcon, { size: 22, weight: 'fill' }),
      title: orders.length === 1 ? 'New website order' : `${orders.length} new website orders`,
      body: detail,
      duration: 8000,
    });

    if (first) {
      void showSystemOrderNotification(
        first.id,
        first.orderNumber,
        detail || 'Open SalesGround to review the new order.',
      );
    }
  };

  const showNewOrderHint = (hint: NewOrderHint): void => {
    const number = hint.orderNumber ? `#${hint.orderNumber}` : 'A new order';
    showStatusToast({
      icon: createElement(BellRingingIcon, { size: 22, weight: 'fill' }),
      title: 'New website order',
      body: number,
      duration: 8000,
    });
    void showSystemOrderNotification(
      hint.id,
      hint.orderNumber,
      `${number} is waiting for acceptance.`,
    );
  };

  return {
    async playChime(): Promise<boolean> {
      // Device mute (V1-09 D-01… D-09): the `sg.sound` toggle in /pos/device is
      // a CROSS-PACKET CONTRACT declared in device/prefs.ts and 02-design-system
      // §6, and it was never consumed — the switch did nothing.
      // Returning false (rather than true) is deliberate: muting the SOUND must
      // not silently drop the notification, so RealtimeStore still runs
      // `onChimeBlocked` and the order is announced visually. The Device copy
      // states this.
      if (!isSoundEnabled()) return false;
      try {
        if (await playBellTone()) return true;
        audio ??= new Audio(chimeUrl);
        audio.currentTime = 0;
        const result = audio.play();
        // Some environments return undefined instead of a promise.
        if (result) await result;
        return true;
      } catch {
        return false; // NotAllowedError (autoplay) / NotSupportedError / jsdom
      }
    },
    onNewOrderHint: showNewOrderHint,
    onNewOrders: showNewOrderNotification,
    onChimeBlocked: showNewOrderNotification,
  };
}
